import { useRegionalConfiguration } from '@/entities/regional-configuration/hooks/useRegionalConfiguration';
import useRegion from '@/hooks/providers/useRegion';
import useTrackingProvider from '@/hooks/providers/useTrackingProvider';

const TRACKING_SITE_NAME_KEY = 'options.trackingConfig.siteName';

export type TrackingSiteName = {
    siteName: string | undefined;
    /**
     * `false` until **both** the region query and that region's configuration
     * query have settled (resolved or errored). One-shot emitters — `APP_LAUNCH`
     * and the bootstrap `INFO_UPDATE` snapshot — must wait for this before reading
     * `siteName`, otherwise they can commit a `brandPlatformId` built from the
     * pinned SSR fallback (or the region-config default) and never re-emit once
     * the live value resolves.
     *
     * The region gate matters because `useRegion()` returns a `'us'` default until
     * its `GetRegion` request resolves; gating on the config query alone could
     * mark ready while the region is still defaulted, committing a premature US
     * `siteName` on a non-US route.
     */
    isReady: boolean;
};

/**
 * Resolves the tracking `siteName` (which drives `brandPlatformId` via
 * `getTrackingBrandPlatform`) from the **current** region's configuration.
 *
 * `trackingData.siteName` is server config that `_app` pins to the first MFE
 * mount via `useRef`, so it never tracks region changes within a session (and
 * can diverge from the server region on a referer-less relaunch). That
 * decoupling let a US session emit `brandPlatformId = plutotvintl_site_desktop`
 * while the request itself correctly resolved US (`countryCode`, `clientRegion`,
 * and the `/us/...` URL).
 *
 * Resolving against the live `useRegion()` region makes `brandPlatformId` track
 * the current region instead of a value pinned at first mount. `siteName` falls
 * back to the pinned value while either query is unresolved. `isReady` lets
 * one-shot emitters defer until the resolved value is available so they never
 * emit the fallback.
 */
export const useTrackingSiteName = (): TrackingSiteName => {
    const pinnedSiteName = useTrackingProvider().trackingData?.siteName;
    const { region, isLoading: isRegionLoading, isError: isRegionError } = useRegion();
    const {
        getConfigByKey,
        isLoading: isConfigLoading,
        isError: isConfigError,
    } = useRegionalConfiguration({ region });
    const regionSiteName = getConfigByKey(TRACKING_SITE_NAME_KEY) as string | undefined;

    // Errors count as settled so a failed lookup falls back to the pinned value
    // instead of blocking tracking indefinitely.
    const isRegionSettled = !isRegionLoading || isRegionError;
    const isConfigSettled = !isConfigLoading || isConfigError;

    // While GetRegion is pending or failed, `region` is the 'us' default, so the
    // config value belongs to the wrong region — prefer the pinned value instead.
    const isRegionResolved = !isRegionLoading && !isRegionError;

    return {
        siteName: (isRegionResolved ? regionSiteName : undefined) ?? pinnedSiteName,
        isReady: isRegionSettled && isConfigSettled,
    };
};
